/**
 * Feasibility calculations (client-side; no backend endpoint exists).
 *
 * FORMULA SPEC: the reconciled definitions proposed in the review of the BRD, whose own
 * formulas contradict each other. They are PENDING CONFIRMATION by the product owner.
 *
 *   required invites     = completes / (RR x IR x (1 - dropout))
 *   feasible completes   = panelSize x RR x IR x (1 - dropout) x LOIfactor
 *   feasibility ratio    = feasible completes / wanted completes   (>= 1 means feasible)
 *   margin of error (95%) = 1.96 x sqrt(0.25 / n)
 *   LOIfactor            = min(1, REFERENCE_LOI / LOI)   (longer surveys reduce feasibility)
 *
 * PLANNING_ASSUMPTIONS are placeholders, NOT measured data. Replace with historical rates
 * per country / LOI band when available. All rates are fractions (0.25 = 25%).
 */
export const PLANNING_ASSUMPTIONS = {
  responseRate: 0.25, // invited -> started (BRD worked example uses 0.25)
  dropoutRate: 0.1,
  referenceLoi: 10, // minutes at which the LOI factor is 1
};

const isPositive = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;
const pct = (n) => Number(n) / 100;

/** `ir` is a percentage (5-100) as entered in the form. Returns null when inputs are unusable. */
export function requiredInvites({ completes, ir, responseRate = PLANNING_ASSUMPTIONS.responseRate, dropoutRate = PLANNING_ASSUMPTIONS.dropoutRate }) {
  const c = Number(completes);
  const yieldPerInvite = responseRate * pct(ir) * (1 - dropoutRate);
  if (!isPositive(c) || !isPositive(yieldPerInvite)) return null;
  return Math.ceil(c / yieldPerInvite);
}

export const loiFactor = (loi, referenceLoi = PLANNING_ASSUMPTIONS.referenceLoi) => {
  const l = Number(loi);
  return isPositive(l) ? Math.min(1, referenceLoi / l) : null;
};

/** Needs a real `panelSize` (matching panelists). Returns null if it is unknown: never guesses. */
export function feasibleCompletes({ panelSize, ir, loi, responseRate = PLANNING_ASSUMPTIONS.responseRate, dropoutRate = PLANNING_ASSUMPTIONS.dropoutRate, referenceLoi }) {
  const factor = loiFactor(loi, referenceLoi);
  if (!isPositive(panelSize) || !isPositive(pct(ir)) || factor === null) return null;
  return Math.floor(panelSize * responseRate * pct(ir) * (1 - dropoutRate) * factor);
}

export const feasibilityRatio = (feasible, wanted) => (isPositive(feasible) && isPositive(Number(wanted)) ? feasible / Number(wanted) : null);

/** Worst-case (p = 0.5) margin of error at 95% confidence, as a percentage. */
export const marginOfError = (n) => (isPositive(Number(n)) ? 1.96 * Math.sqrt(0.25 / Number(n)) * 100 : null);
